import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import {
  getStarterPath,
  JOURNEYS,
  type StarterStep,
} from "@/lib/starter-path.functions";
import { hreflangLinks } from "@/lib/seo";
import { Link } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";
import { InfoCard, PageIntro, PageSection } from "@/components/site/page";
import { THEME_PAGES } from "@/lib/themes-public";
import { THEME_DOT, THEME_IMG } from "@/components/site/theme-ui";

export const Route = createFileRoute("/baslangic")({
  head: () => ({
    meta: [
      { title: "Buradan başla — Dört temalı okuma yolu" },
      {
        name: "description",
        content:
          "Yeniyseniz dört temadan birini seçin: İnsan ve Gezegen, Teknoloji ve Güç, Ekonomi ve Toplum, İnsan ve Anlam.",
      },
      {
        property: "og:title",
        content: "Buradan başla — Dört temalı okuma yolu",
      },
      {
        property: "og:description",
        content:
          "Dört tema, dört soru: Akıllı Sürdürülebilirlik yazılarına nereden başlayacağınızı seçin.",
      },
      {
        property: "og:url",
        content: "https://akillisurdurulebilirlik.com/baslangic",
      },
    ],
    links: [
      {
        rel: "canonical",
        href: "https://akillisurdurulebilirlik.com/baslangic",
      },
      ...hreflangLinks("/baslangic"),
    ],
  }),
  loader: () => getStarterPath(),
  component: StarterPage,
});

function JourneyBlock({
  label,
  intro,
  steps,
}: {
  label: string;
  intro: string;
  steps: StarterStep[];
}) {
  const filled = steps.filter((s) => s.title.trim() !== "");
  const empty = filled.length === 0;

  return (
    <PageSection title={label} aside={intro}>
      {empty && (
        <span className="inline-block rounded-full border border-ink/20 px-2 py-1 text-[10px] uppercase tracking-[0.25em] text-ink/85">
          Yakında
        </span>
      )}
      {!empty && (
        <ol className="space-y-3">
          {filled.map((s) => {
            const isExternal = /^https?:\/\//.test(s.url);
            const href = s.url || "#";
            const disabled = href === "#";
            const linkProps = disabled
              ? { onClick: (e: React.MouseEvent) => e.preventDefault() }
              : isExternal
                ? { target: "_blank", rel: "noopener noreferrer" }
                : {};
            return (
              <li key={s.position}>
                <a
                  href={href}
                  {...linkProps}
                  className={`group flex items-start gap-5 border-b border-ink/10 py-5 transition-colors md:gap-6 ${
                    disabled ? "cursor-default opacity-70" : ""
                  }`}
                >
                  <span className="mt-1 shrink-0 font-serif text-2xl leading-none text-ink/60 tabular-nums md:text-3xl">
                    {String(s.position).padStart(2, "0")}
                  </span>
                  <span className="flex-1">
                    <h3 className="text-balance font-serif text-lg leading-snug transition-colors group-hover:text-clay md:text-xl">
                      {s.title}
                    </h3>
                    {!disabled && (
                      <span className="mt-3 inline-flex min-h-11 items-center gap-2 border-b border-ink/20 pb-0.5 text-[10px] font-bold uppercase tracking-[0.25em] group-hover:border-clay">
                        Oku <span aria-hidden>→</span>
                      </span>
                    )}
                  </span>
                </a>
              </li>
            );
          })}
        </ol>
      )}
    </PageSection>
  );
}

function StarterPage() {
  const initial = Route.useLoaderData();
  const fetchSteps = useServerFn(getStarterPath);
  const { data: steps = initial } = useQuery({
    queryKey: ["starter-path"],
    queryFn: () => fetchSteps(),
    initialData: initial,
  });

  return (
    <>
      <PageIntro
        crumbs={[{ label: "Buradan başla" }]}
        kicker="Giriş yolu"
        title="Buradan başla."
        lede="Yazılar dört tema etrafında toplanıyor. Size en yakın sorudan girin; her tema kendi yazılarına, kitabına ve öteki temalarla bağlarına açılıyor."
      />

      <PageSection title="Dört tema">
        <ul className="divide-y divide-ink/10 border-y border-ink/10">
          {THEME_PAGES.map((th) => (
            <li key={th.key}>
              <Link to="/tema/$slug" params={{ slug: th.slug }} className="group grid grid-cols-[64px_minmax(0,1fr)_auto] items-center gap-x-4 py-4 sm:grid-cols-[96px_minmax(0,1fr)_auto] sm:gap-x-6">
                <img src={THEME_IMG[th.key]} alt="" loading="lazy" width={192} height={192} className="size-16 rounded-full object-cover sm:size-24" />
                <span className="min-w-0">
                  <span className="flex items-center gap-2 font-serif text-[20px] sm:text-[26px] group-hover:text-clay">
                    <span aria-hidden className={`size-2.5 shrink-0 rounded-full ${THEME_DOT[th.key]}`} />
                    {th.title}
                  </span>
                  <span className="mt-1 block font-serif text-[15px] italic leading-snug text-ink/70 sm:text-[18px]">{th.question}</span>
                </span>
                <ArrowRight aria-hidden className="size-5 shrink-0 transition-transform group-hover:translate-x-1" />
              </Link>
            </li>
          ))}
        </ul>
      </PageSection>

      <PageSection title="Önce kavramı netleştirmek isterseniz">
        <InfoCard>
          <h3 className="font-serif text-xl text-ink md:text-2xl">
            <Link to="/yazilar/surdurulebilirlik-nedir-rehberi" className="hover:text-clay">
              Sürdürülebilirlik nedir? — Tanım, tarih ve eleştirel bir rehber
            </Link>
          </h3>
          <p className="mt-2 text-sm leading-relaxed text-ink/70">
            Üç sütun, kısa tarih, yaygın yanılgılar ve yapay zekâ çağında insan-merkezli bir okuma. Bu sitenin geri kalanı için kavramsal zemin.
          </p>
        </InfoCard>
      </PageSection>

      {JOURNEYS.map((j) => {
        const js = steps.filter((st: StarterStep) => st.journey === j.id && st.title.trim() !== "");
        if (!js.length) return null;
        return (
          <div key={j.id} id={j.id} className="scroll-mt-24">
            <JourneyBlock label={j.label} intro={j.intro} steps={js} />
          </div>
        );
      })}
    </>
  );
}
